/**
 * @file _eligibility-storage.js
 * @description Manages persistence of user eligibility data in localStorage.
 *
 * Stores goals (id, status, title, target), survey facts, the URL the survey
 * was taken on, and an expiry timestamp. Automatically evicts expired data on
 * instantiation and on each read.
 *
 * @exposes window.QLD.eligibilityStorage - EligibilityStorage singleton
 *
 * @example
 * window.QLD.eligibilityStorage.save(eventDetail);      // from survey:results:ready
 * window.QLD.eligibilityStorage.get();                  // { goals, facts, surveyUrl, ... } | null
 * window.QLD.eligibilityStorage.hasData();              // boolean — false if invalidated
 * window.QLD.eligibilityStorage.isInvalidated();        // true if soft-invalidated (expired or version mismatch)
 * window.QLD.eligibilityStorage.getInvalidatedData();   // raw stale record, or null
 * window.QLD.eligibilityStorage.clear();                // hard-removes storage, dispatches eligibility:cleared
 *
 * Events dispatched on window:
 *   eligibility:saved      — new results stored
 *   eligibility:cleared    — user-initiated hard clear
 *   eligibility:invalidated — auto-invalidated due to expiry or version mismatch;
 *                             stale data is preserved; detail: { reason: 'expired'|'version_mismatch' }
 */

(function () {
    'use strict';

    const CONFIG = {
        STORAGE_KEY: 'qld_eligibility_data',
        EXPIRY_DAYS: 30
    };

    class EligibilityStorage {
        constructor() {
            this.storageKey = CONFIG.STORAGE_KEY;
            this._cache = null;
            this._currentSurveyVersion = null;
            this._versionFetchInFlight = null;
            this.refreshSurveyVersion({ invalidateOnMismatch: true });
            this.get();
        }

        /**
         * Save eligibility data from survey results event
         * Clears existing data first, then stores facts, goals (id+status+title), and root metadata
         * @param {Object} detail - The event.detail object from survey:results:ready
         */
        save(detail) {
            // Silent clear — no event dispatch to avoid double updateUI
            this._cache = null;
            localStorage.removeItem(this.storageKey);

            const { facts, results } = detail;
            const allGoals = results.allGoals;
            const surveyVersion = this.normalizeVersion(detail && detail.version) || this.getCurrentSurveyVersion();

            if (surveyVersion) {
                this._currentSurveyVersion = surveyVersion;
            }

            const now = new Date();
            const expiresAt = new Date(now.getTime() + (CONFIG.EXPIRY_DAYS * 24 * 60 * 60 * 1000));

            const data = {
                version: surveyVersion,
                timestamp: detail.timestamp || now.toISOString(),
                expiresAt: expiresAt.toISOString(),
                surveyUrl: window.location.href,
                facts: facts,
                goals: allGoals.map(goal => ({
                    id: goal.goalId,
                    status: goal.status,
                    title: goal.meta?.title || '',
                    target: goal.meta?.target || ''
                }))
            };

            try {
                localStorage.setItem(this.storageKey, JSON.stringify(data));
                this._cache = data;

                // Dispatch event for cross-component communication
                window.dispatchEvent(new CustomEvent('eligibility:saved', {
                    detail: data
                }));

                return { success: true, data };
            } catch (error) {
                console.error('Failed to save eligibility data:', error);
                return { success: false, error };
            }
        }

        /**
         * Retrieve stored eligibility data.
         * Returns null if not found, already invalidated, expired, or version-mismatched.
         * Expiry and version mismatch soft-invalidate (preserve data) rather than hard-delete.
         */
        get() {
            if (this._cache) return this._cache;

            // Non-blocking refresh so version invalidation can happen from canonical REST data.
            this.refreshSurveyVersion({ invalidateOnMismatch: true });

            try {
                const raw = localStorage.getItem(this.storageKey);
                if (!raw) return null;

                const data = JSON.parse(raw);

                if (data._invalidated) {
                    return null;
                }

                if (this.isExpired(data)) {
                    this.markInvalid('expired');
                    return null;
                }

                if (this.isVersionMismatch(data)) {
                    this.markInvalid('version_mismatch');
                    return null;
                }

                this._cache = data;
                return data;
            } catch (error) {
                console.error('Failed to retrieve eligibility data:', error);
                return null;
            }
        }

        /**
         * Get all goals
         */
        getGoals() {
            const data = this.get();
            return data?.goals || [];
        }

        /**
         * Check if any eligibility data exists
         */
        hasData() {
            return this.get() !== null;
        }

        /**
         * Soft-invalidate stored data. Preserves the record in localStorage so the UI
         * can prompt the user to retake the survey. Dispatches eligibility:invalidated.
         * @param {'expired'|'version_mismatch'} reason
         */
        markInvalid(reason) {
            this._cache = null;

            try {
                const raw = localStorage.getItem(this.storageKey);
                if (raw) {
                    const data = JSON.parse(raw);
                    data._invalidated = true;
                    data._invalidatedReason = reason;
                    data._invalidatedAt = new Date().toISOString();
                    localStorage.setItem(this.storageKey, JSON.stringify(data));
                }
            } catch (error) {
                console.warn('Failed to mark eligibility data as invalid:', error);
            }

            window.dispatchEvent(new CustomEvent('eligibility:invalidated', {
                detail: { reason }
            }));
        }

        /**
         * Returns true if data exists but has been soft-invalidated.
         */
        isInvalidated() {
            try {
                const raw = localStorage.getItem(this.storageKey);
                if (!raw) return false;
                const data = JSON.parse(raw);
                return data._invalidated === true;
            } catch (error) {
                return false;
            }
        }

        /**
         * Returns the raw invalidated record, or null if none exists.
         * Intended for UI use — e.g. showing when the user last completed the survey.
         */
        getInvalidatedData() {
            try {
                const raw = localStorage.getItem(this.storageKey);
                if (!raw) return null;
                const data = JSON.parse(raw);
                return data._invalidated === true ? data : null;
            } catch (error) {
                return null;
            }
        }

        /**
         * Hard-remove eligibility data. Use for explicit user-initiated clears.
         * Dispatches eligibility:cleared.
         */
        clear() {
            this._cache = null;
            localStorage.removeItem(this.storageKey);
            window.dispatchEvent(new CustomEvent('eligibility:cleared', { detail: null }));
        }

        /**
         * Invalidate cache (e.g. after cross-tab storage change)
         */
        invalidateCache() {
            this._cache = null;
        }

        /**
         * Returns true if the stored data has passed its expiry date
         */
        isExpired(data) {
            return !data || new Date(data.expiresAt) < new Date();
        }

        /**
         * Returns the latest fetched survey/ruleset version.
         */
        getCurrentSurveyVersion() {
            return this._currentSurveyVersion;
        }

        getSurveyVersionEndpoint() {
            return (window.NSWP && window.NSWP.urls && window.NSWP.urls.eligibilitySurveyMetadata) || null;
        }

        async refreshSurveyVersion(options) {
            const opts = options || {};

            if (this._versionFetchInFlight) {
                return this._versionFetchInFlight;
            }

            const endpoint = this.getSurveyVersionEndpoint();
            if (!endpoint) {
                return null;
            }

            this._versionFetchInFlight = this.fetchSurveyVersion(endpoint)
                .then(version => {
                    this._currentSurveyVersion = version;

                    if (opts.invalidateOnMismatch !== false) {
                        this.invalidateIfStoredVersionMismatch(version);
                    }

                    return version;
                })
                .catch(error => {
                    console.warn('Failed to refresh survey data version:', error);
                    return null;
                })
                .finally(() => {
                    this._versionFetchInFlight = null;
                });

            return this._versionFetchInFlight;
        }

        async fetchSurveyVersion(endpoint) {
            const response = await fetch(endpoint, {
                method: 'GET',
                credentials: 'same-origin',
                cache: 'no-store',
                headers: {
                    Accept: 'application/json, text/plain;q=0.9, */*;q=0.8'
                }
            });

            if (!response.ok) {
                throw new Error(`Version fetch failed: ${response.status} ${response.statusText}`);
            }

            const payload = await response.json();
            return this.normalizeVersion(payload && payload.version);
        }

        invalidateIfStoredVersionMismatch(currentVersion) {
            const fetchedVersion = this.normalizeVersion(currentVersion);
            if (!fetchedVersion) {
                return;
            }

            try {
                const raw = localStorage.getItem(this.storageKey);
                if (!raw) {
                    return;
                }

                const data = JSON.parse(raw);
                if (data._invalidated) {
                    return;
                }

                const storedVersion = this.normalizeVersion(data && data.version);

                if (storedVersion && storedVersion !== fetchedVersion) {
                    this.markInvalid('version_mismatch');
                }
            } catch (error) {
                console.warn('Failed to validate stored version against fetched version:', error);
            }
        }

        isVersionMismatch(data) {
            const storedVersion = this.normalizeVersion(data && data.version);
            const currentVersion = this.getCurrentSurveyVersion();

            if (!storedVersion || !currentVersion) {
                return false;
            }

            return storedVersion !== currentVersion;
        }

        normalizeVersion(version) {
            if (typeof version === 'number') {
                return String(version);
            }

            if (typeof version !== 'string') {
                return null;
            }

            const trimmed = version.trim();
            return trimmed.length > 0 ? trimmed : null;
        }

    }

    // Singleton instance — exposed on window for cross-file access
    window.QLD = window.QLD || {};
    window.QLD.eligibilityStorage = new EligibilityStorage();

})();

